import { useId, type KeyboardEvent } from 'react';
import { Icon } from './Icon';

/**
 * A short single choice as a row of buttons (WAI-ARIA radio group): one tab stop (the chosen one, else the first),
 * arrow keys move the choice and the focus, its label and inline error linked. `field` marks the first button with
 * `data-field` (a refusal or a failed submit focuses it); `className` per option for its colour when chosen.
 */
export function ChoiceGroup<V extends string>({ field, label, options, value, onChange, error, required = true, optionClass }: {
  field?: string;
  label: string;
  options: readonly (readonly [V, string])[];
  value: V | null;
  onChange: (v: V) => void;
  error: string | null;
  required?: boolean;
  optionClass?: (v: V) => string;
}) {
  const id = useId();
  const labelId = `${id}-label`;
  const errId = `${id}-err`;
  const focusable = value ?? options[0]?.[0];
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step || !options.length) return;
    e.preventDefault();
    const n = options.length;
    const at = options.findIndex(([v]) => v === value);
    // nothing chosen yet: right goes to the first, left to the last
    const next = options[at < 0 ? (step > 0 ? 0 : n - 1) : (at + step + n) % n][0];
    onChange(next);
    e.currentTarget.querySelector<HTMLElement>(`[data-value="${next}"]`)?.focus();
  };
  return (
    <div className="field">
      <span className="choice-label" id={labelId}>
        {label}
        {required && (
          <span className="req" aria-hidden="true">
            *
          </span>
        )}
      </span>
      <div className="choice-grid choice-row" role="radiogroup" aria-labelledby={labelId} aria-describedby={error ? errId : undefined} aria-required={required || undefined} onKeyDown={onKey}>
        {options.map(([v, l], i) => (
          <button
            key={v}
            type="button"
            role="radio"
            data-value={v}
            data-field={i === 0 ? field : undefined}
            aria-checked={value === v}
            tabIndex={v === focusable ? 0 : -1}
            className={`choice ${optionClass?.(v) ?? ''}`}
            onClick={() => onChange(v)}
          >
            {l}
          </button>
        ))}
      </div>
      {error && (
        <p className="field-error" id={errId}>
          <Icon name="warn" size={15} />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
}
